import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as Icons from 'react-feather';
import PageHeader from '../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts';
import { getModulePermissions } from '../../utils/permissionHelper';
import { logAuditEvent } from '../../utils/auditLogger';
import { buildFileDownloadUrl, getApiBaseUrl } from '../../utils/apiConfig';
import { getStorageValues } from '../../shared/CommonFunctions';
import FluidGallery from './components/FluidGallery';

const isImage = (mime = '', name = '') => {
  const m = String(mime || '').toLowerCase();
  if (m.startsWith('image/')) return true;
  const n = String(name || '').toLowerCase();
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'].some((ext) => n.endsWith(ext));
};

const buildFileName = (row) => row?.OriginalName || row?.StoredName || `File-${row?.FileLibraryId || 'NA'}`;

const stripExtension = (filename = '') => {
  const s = String(filename || '').trim();
  if (!s) return '';
  const lastDot = s.lastIndexOf('.');
  if (lastDot <= 0) return s; // no dot, or dot as first char
  return s.slice(0, lastDot);
};

const entityTypeLabel = (t = '') => {
  const key = String(t || '').trim().toLowerCase();
  if (key === 'handoverregisteritem') return 'Handover Items';
  if (!key) return '';
  return key.charAt(0).toUpperCase() + key.slice(1);
};

function BreadcrumbLink({ children, onClick, title }) {
  return (
    <button type="button" className="sk-breadcrumb-link" onClick={onClick} title={title}>
      {children}
    </button>
  );
}

function FolderTile({ title, thumbnailUrl, subtitle, onClick }) {
  return (
    <button type="button" className="sk-folder" onClick={onClick} title={title}>
      <div className="sk-folder-icon">
        <div className="sk-file-preview">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt="" className="sk-file-preview-img" />
          ) : null}
        </div>
      </div>
      <p className="sk-folder-title">{title}</p>
      {subtitle ? <div className="sk-folder-subtitle">{subtitle}</div> : null}
    </button>
  );
}

function GallaryMain(props) {
  const [alert, setAlert] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Gallery';

  const [tab, setTab] = useState('photos'); // photos | files
  const [entityTypes, setEntityTypes] = useState([]);
  const [selectedEntityType, setSelectedEntityType] = useState('');
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const modulePermissions = getModulePermissions(moduleName);
    setUserModulePermission(modulePermissions || {});
  }, [moduleName]);

  useEffect(() => {
    logAuditEvent(moduleName, 'Accessed Module', `Accessed ${moduleName}`);
  }, [moduleName]);

  const apiBase = getApiBaseUrl();
  const userId = getStorageValues('userId') || 1;

  const resetToRoot = () => {
    setSelectedEntityType('');
    setEntities([]);
    setSelectedEntity(null);
    setFiles([]);
  };

  const fetchEntityTypes = useCallback(async () => {
    if (!apiBase) return;
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/gallery/entity-types`, {
        params: { filter: tab }
      });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      // Only show types with files; backend already ensures distinct from filelibrary
      setEntityTypes(list);
    } catch (e) {
      setEntityTypes([]);
      CreateAlert('Unable to load folders', 'Error', setAlert);
    } finally {
      setLoading(false);
    }
  }, [apiBase, tab]);

  const switchTab = useCallback((nextTab) => {
    if (nextTab === tab) return;
    setTab(nextTab);
    // Reset drill-down views so tab switch always starts from root folders.
    resetToRoot();
    fetchEntityTypes();
  }, [tab, fetchEntityTypes]);

  const fetchEntities = useCallback(async (entityType) => {
    if (!apiBase || !entityType) return;
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/gallery/entities`, {
        params: { entityType, UserId: userId, filter: tab }
      });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setEntities(list);
    } catch (e) {
      setEntities([]);
      CreateAlert('Unable to load folders', 'Error', setAlert);
    } finally {
      setLoading(false);
    }
  }, [apiBase, userId, tab]);

  useEffect(() => {
    // When user switches Photos/Files while browsing entity folders (2nd screen),
    // re-fetch entities so "empty" folders are hidden per selected tab.
    if (!selectedEntityType || selectedEntity) return;
    fetchEntities(selectedEntityType);
  }, [tab, selectedEntityType, selectedEntity, fetchEntities]);

  const fetchFilesForEntity = useCallback(async (entityType, entityId) => {
    if (!apiBase || !entityType || !entityId) return;
    setLoading(true);
    try {
      const res = await axios.post(`${apiBase}/file/list`, { entityType, entityId });
      const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data?.data?.[0]) ? res.data.data[0] : [];
      setFiles(Array.isArray(list) ? list : []);
    } catch (e) {
      setFiles([]);
      CreateAlert('Unable to load files', 'Error', setAlert);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchEntityTypes();
  }, [fetchEntityTypes]);

  const photos = useMemo(() => {
    return (files || [])
      .filter((f) => isImage(f?.MimeType, f?.OriginalName))
      .map((f) => {
        const id = f?.FileLibraryId;
        const name = stripExtension(buildFileName(f));
        const url = buildFileDownloadUrl(id);
        return { id, title: name, url, thumbUrl: url };
      });
  }, [files]);

  const docs = useMemo(() => {
    return (files || [])
      .filter((f) => {
        const mime = String(f?.MimeType || '').toLowerCase();
        if (!mime) return true;
        if (mime === 'application/pdf') return true;
        if (mime.startsWith('image/')) return false;
        if (mime.startsWith('video/')) return false;
        // Exclude photos; show everything else as file/doc
        return true;
      })
      .map((f) => {
        const id = f?.FileLibraryId;
        const name = buildFileName(f);
        const previewUrl = buildFileDownloadUrl(id);
        const downloadUrl = buildFileDownloadUrl(id, { inline: false });
        return {
          id,
          name,
          mimeType: f?.MimeType || '',
          previewUrl,
          downloadUrl
        };
      });
  }, [files]);

  const folderTiles = useMemo(() => {
    if (!selectedEntityType) {
      return (entityTypes || []).map((t) => ({
        key: t,
        title: entityTypeLabel(t),
        subtitle: '',
        thumbnailUrl: null,
        onClick: () => {
          setSelectedEntityType(t);
          setSelectedEntity(null);
          setFiles([]);
          fetchEntities(t);
        }
      }));
    }

    if (selectedEntityType && !selectedEntity) {
      return (entities || []).map((e) => {
        const previewId = e?.previewImageFileLibraryId;
        const thumbnailUrl = previewId ? buildFileDownloadUrl(previewId) : null;
        return {
          key: `${e.entityType}-${e.entityId}`,
          title: e.label || e.entityId,
          subtitle: '',
          thumbnailUrl,
          onClick: () => {
            setSelectedEntity(e);
            fetchFilesForEntity(selectedEntityType, e.entityId);
          }
        };
      });
    }
    return [];
  }, [selectedEntityType, selectedEntity, entityTypes, entities, fetchEntities, fetchFilesForEntity]);

  const showFolderView = !selectedEntity;

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onRefresh={() => {
          resetToRoot();
          fetchEntityTypes();
        }}
        showPDF={false}
        showExcel={false}
        showPrint={false}
        showRefresh={true}
        showAddButton={Number(userModulePermission?.AddModule) === 1}
      />

      <div className="content">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="sk-breadcrumb">
            {!selectedEntityType ? (
              <span className="sk-breadcrumb-current">Folders</span>
            ) : (
              <>
                <BreadcrumbLink
                  title="Go to root folders"
                  onClick={() => {
                    resetToRoot();
                    fetchEntityTypes();
                  }}
                >
                  Folders
                </BreadcrumbLink>
                <span className="sk-breadcrumb-sep">{'>'}</span>
                {!selectedEntity ? (
                  <span className="sk-breadcrumb-current">{entityTypeLabel(selectedEntityType)}</span>
                ) : (
                  <>
                    <BreadcrumbLink
                      title={`Open ${entityTypeLabel(selectedEntityType)}`}
                      onClick={() => {
                        setSelectedEntity(null);
                        setFiles([]);
                        fetchEntities(selectedEntityType);
                      }}
                    >
                      {entityTypeLabel(selectedEntityType)}
                    </BreadcrumbLink>
                    <span className="sk-breadcrumb-sep">{'>'}</span>
                    <span className="sk-breadcrumb-current">{selectedEntity?.label || selectedEntity?.entityId || ''}</span>
                  </>
                )}
              </>
            )}
          </div>
          {(selectedEntityType || selectedEntity) ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                if (selectedEntity) {
                  setSelectedEntity(null);
                  setFiles([]);
                  return;
                }
                resetToRoot();
              }}
            >
              <Icons.ArrowLeft size={14} className="me-1" />
              Back
            </button>
          ) : null}
        </div>

        <nav className="sk-gallery-tabs nav nav-style-6 nav-pills mb-3 nav-justified w-50 d-sm-flex d-block" role="tablist">
          <button
            type="button"
            className={`nav-link ${tab === 'photos' ? 'active' : ''}`}
            role="tab"
            aria-selected={tab === 'photos'}
            onClick={() => switchTab('photos')}
          >
            Photos
          </button>
          <button
            type="button"
            className={`nav-link ${tab === 'files' ? 'active' : ''}`}
            role="tab"
            aria-selected={tab === 'files'}
            onClick={() => switchTab('files')}
          >
            Files
          </button>
        </nav>

        {loading ? <div className="text-muted small">Loading...</div> : null}

        {showFolderView ? (
          <div className="sk-folder-container">
            {folderTiles.length === 0 && !loading ? (
              <div className="text-muted small">No folders found.</div>
            ) : (
              folderTiles.map((t) => (
                <FolderTile
                  key={t.key}
                  title={t.title}
                  subtitle={t.subtitle}
                  thumbnailUrl={t.thumbnailUrl}
                  onClick={t.onClick}
                />
              ))
            )}
          </div>
        ) : tab === 'photos' ? (
          <div className="sk-gallery-shell">
            <div className="sk-gallery-shell-scroll">
              <FluidGallery images={photos} />
            </div>
          </div>
        ) : (
          <div className="sk-file-grid">
            {docs.length === 0 && !loading ? <div className="text-muted small">No files found.</div> : null}
            {docs.map((f) => (
              <div className="sk-file-card" key={f.id}>
                <div className="sk-file-actions">
                  <button
                    type="button"
                    className="sk-action-btn"
                    title="Preview"
                    onClick={() => window.open(f.previewUrl, '_blank', 'noreferrer')}
                  >
                    <Icons.Eye size={16} color="#1261a0" />
                  </button>
                  <a className="sk-action-btn" title="Download" href={f.downloadUrl} target="_blank" rel="noreferrer">
                    <Icons.Download size={16} color="#1261a0" />
                  </a>
                </div>
                <div className="fw-semibold small sk-file-name" title={f.name}>
                  {f.name}
                </div>
                <div className="text-muted small">{f.mimeType || '-'}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default GallaryMain;